import { z } from 'zod';

export const SyncStateValues = {
  PENDING: 'pending',
  IN_PROGRESS: 'in-progress',
  COMPLETE: 'complete',
  FAILED: 'failed',
} as const;

export type SyncState = (typeof SyncStateValues)[keyof typeof SyncStateValues];

const syncStateLiterals = Object.values(SyncStateValues) as [SyncState, ...Array<SyncState>];

export const BackendStateValues = {
  LOCAL: 'local',
  UPLOADED: 'uploaded',
} as const;

export type BackendState = (typeof BackendStateValues)[keyof typeof BackendStateValues];

const backendStateLiterals = Object.values(BackendStateValues) as [BackendState, ...Array<BackendState>];

export type HuntingProductMedia = z.infer<typeof huntingProductMediaSchema>;
export const huntingProductMediaSchema = z.object({
  id: z.string(), // `{serial}/{relativePath}` — local dedup key (one user's devices, not a global PK)
  serial: z.string(), // device serial, denormalized from id for per-device gallery grouping
  capturedAt: z.number(), // epoch ms, parsed from the filename by the future engine
  type: z.enum(['image', 'video'] as const),
  localFilePath: z.string(), // planned Directory.Data path, known at creation
  thumbnailPath: z.string().nullable(), // null until a thumbnail is generated (S2/S3)
  syncState: z.enum(syncStateLiterals), // device→phone axis
  backendState: z.enum(backendStateLiterals), // phone→backend axis (forward-looking)
  uploadedAt: z.number().nullable(), // ms epoch, null until uploaded
});

// capturedAt is the device clock, unreliable on current prototypes — device-local, never trusted
// wall-clock. Exported so the sync placeholder parses the same date as the on-disk item.
export function parseCapturedAtFromFileName(fileName: string): number {
  const match = /^(?:img|vid)_(\d{4}-\d{2}-\d{2})_(\d{6})_\d+/.exec(fileName);
  if (!match) return 0;
  const [, date, time] = match;
  const iso = `${date}T${time.slice(0, 2)}:${time.slice(2, 4)}:${time.slice(4, 6)}`;
  const timestamp = new Date(iso).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

// The device appends `_shot`/`_preshot` as the last filename segment when a recording (or the
// preshot frame captured for it) is tied to a detected shot (mediaservice FinalizeRecording,
// media_service.cpp; preshot_images.cpp) — files without a detected shot carry no such suffix.
export function hasShotSuffix(fileName: string): boolean {
  return /_(?:pre)?shot\.[^./]+$/.test(fileName);
}
